import { Bike, Calendar, Check, Clock, MapPin, ShoppingBag, User, XCircle } from "lucide-react";

import { CORES, bolinhas, etapaAtual, janelaAgendada, posicao, reais } from "./etapas";

function Bolinhas({ pedido, retirada, cor, t }) {
  const lista = bolinhas(pedido, retirada);
  const atual = posicao(pedido, retirada, lista.length);
  return (
    <div className="flex items-start" data-e2e="acompanhamento-bolinhas">
      {lista.map((b, i) => {
        const Icone = b.icone;
        const feito = i < atual;
        const agora = i === atual;
        const trilho = (ativo) => (ativo ? "bg-emerald-500" : t.escuro ? "bg-zinc-600" : "bg-gray-200");
        return (
          <div key={b.id} className="flex-1 min-w-0 flex flex-col items-stretch">
            <div className="flex items-center">
              <div className={`flex-1 h-1 rounded-full ${i === 0 ? "opacity-0" : trilho(feito || agora)}`} />
              <div className={`relative w-9 h-9 rounded-full grid place-items-center shrink-0 transition-colors duration-500 ${
                feito ? "bg-emerald-500 text-white"
                  : agora ? `${CORES[cor].icone} text-white ring-4 ${CORES[cor].anel}`
                    : t.escuro ? "bg-zinc-700 border border-zinc-600 text-zinc-400" : "bg-gray-100 border border-gray-200 text-gray-400"}`}>
                {agora && <span className={`absolute inset-0 rounded-full ${CORES[cor].icone} opacity-40 animate-ping`} />}
                {feito ? <Check className="w-4 h-4 relative" strokeWidth={3} /> : <Icone className="w-4 h-4 relative" />}
              </div>
              <div className={`flex-1 h-1 rounded-full ${i === lista.length - 1 ? "opacity-0" : trilho(feito)}`} />
            </div>
            <p className={`text-center text-[11px] sm:text-xs font-semibold mt-1.5 leading-tight ${agora ? CORES[cor].texto : feito ? t.texto : t.mudo}`}>{b.rotulo}</p>
            <p className={`text-center text-[10px] sm:text-[11px] tabular-nums ${t.mudo}`}>{b.hora || "—"}</p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * O primeiro card do acompanhamento: código, valor e tipo do pedido no topo, a
 * etapa atual com uma frase e as bolinhas com o horário de cada etapa. Agendado
 * e cancelado são variações do mesmo card.
 */
export default function CartaoStatus({ pedido, retirada, previsao, loja, t, onCancelar }) {
  const st = pedido?.status;
  const agendado = st === "agendado";
  const cancelado = st === "cancelado";
  const etapa = cancelado
    ? { titulo: "Pedido cancelado", frase: pedido?.motivo_cancelamento || pedido?.motivo ? `Motivo: ${pedido.motivo_cancelamento || pedido.motivo}` : "Este pedido não vai ser preparado.", cor: "red", icone: XCircle }
    : agendado
      ? { titulo: "Pedido agendado", frase: "O restaurante aprova o agendamento e começa o preparo perto do horário. Você recebe um aviso quando ele confirmar.", cor: "indigo", icone: Calendar }
      : etapaAtual(pedido, retirada, { previsao });
  const c = CORES[etapa.cor];
  const Icone = etapa.icone;

  return (
    <section className={`relative overflow-hidden rounded-3xl border ${t.card} ${t.borda}`} data-e2e="acompanhamento-status">
      <div className={`absolute inset-x-0 top-0 h-28 bg-gradient-to-b ${c.faixa} to-transparent pointer-events-none`} />
      <div className="relative p-4 sm:p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold">
          <span className={`px-2.5 py-1 rounded-full tabular-nums ${t.chip} ${t.texto}`}>{pedido?.codigo}</span>
          <span className={`px-2.5 py-1 rounded-full tabular-nums ${t.chip} ${t.texto}`}>{reais(pedido?.total)}</span>
          <span className={`px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${retirada ? "bg-blue-500/15 text-blue-600 dark:text-blue-400" : "bg-orange-500/15 text-orange-600 dark:text-orange-400"}`}>
            {retirada ? <ShoppingBag className="w-3.5 h-3.5" /> : <Bike className="w-3.5 h-3.5" />} {retirada ? "Retirada" : "Entrega"}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl grid place-items-center text-white shrink-0 shadow-lg ${c.icone}`}>
            <Icone className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h2 className={`text-xl sm:text-2xl font-black leading-tight ${t.texto}`}>{etapa.titulo}</h2>
            <p className={`text-sm leading-snug mt-0.5 ${t.mudo}`}>{etapa.frase}</p>
          </div>
        </div>

        {agendado && (
          <div className="rounded-2xl bg-indigo-500/10 border border-indigo-500/30 px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-500">{retirada ? "Retirada prevista" : "Entrega prevista"}</p>
            <p className={`text-lg font-black ${t.texto}`}>{janelaAgendada(pedido?.scheduled_at)}</p>
            {!pedido?.cancel_requested_at ? (
              <button type="button" onClick={onCancelar}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-red-500 hover:underline">
                <XCircle className="w-3.5 h-3.5" /> Solicitar cancelamento
              </button>
            ) : (
              <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-500">
                <Clock className="w-3.5 h-3.5" /> Aguardando o restaurante aprovar o cancelamento
              </p>
            )}
          </div>
        )}

        {!agendado && !cancelado && <Bolinhas pedido={pedido} retirada={retirada} cor={etapa.cor} t={t} />}

        {retirada && st === "pronto" && (
          <p className={`flex items-start gap-2 text-sm rounded-xl px-3 py-2.5 ${t.chip}`}>
            <MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
            <span className={t.texto}><b>{loja.nome}</b>{loja.endereco ? <span className={t.mudo}> · {loja.endereco}</span> : null}</span>
          </p>
        )}
        {!retirada && st === "em_rota" && pedido?.entregador_nome && (
          <p className={`flex items-center gap-2 text-sm rounded-xl px-3 py-2.5 ${t.chip} ${t.texto}`}>
            <User className="w-4 h-4 text-blue-500 shrink-0" /> <b>{pedido.entregador_nome}</b> está levando seu pedido
          </p>
        )}
      </div>
    </section>
  );
}
